import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { AgGridReact } from "ag-grid-react";
import ManageColumnsPicker from "components/ManageColumns/ManageColumnsPicker";
import useManageColumns from "hooks/useManageColumns";
import {
  defaultGridOptions,
  createGridOptions,
  gridPresets,
} from "utils/agGridDefaults";

const AGGridWrapper = forwardRef(
  (
    {
      columnDefs: initialColumnDefs,
      rowData,
      enableManageColumns = false,
      clientId = null,
      entityName = null,
      disabledFields = [],
      features = [],
      preset = null,
      gridOptions = {},
      onGridReady = null,
      onColumnChanged = null,
      lockedColumnsSeparator = false,
      // Opt-out of the wrapper's internal autoSize behavior. When true, the
      // wrapper will not call `autoSizeAllColumns` / `autoSizeColumns` /
      // `triggerAutoSizing` itself — the consuming page is fully responsible
      // for column sizing (e.g. via `sizeColumnsToFit` in its own handlers).
      // Useful when content-based auto-sizing leaves an empty strip to the
      // right of visible columns.
      disableAutoSize = false,
      ...restProps
    },
    ref
  ) => {
    const gridRef = useRef(null);
    const [gridApi, setGridApi] = useState(null);
    const [columnDefs, setColumnDefs] = useState(initialColumnDefs);
    const [showManageColumnPopup, setShowManageColumnPopup] = useState(false);

    const { syncColumnsImmediate, resetColumns, isManageColumnsEnabled } =
      useManageColumns({
        clientId,
        entityName,
        originalColumnDefs: initialColumnDefs,
        setColumnDefs,
        gridApi,
        enableManageColumns,
        enableAutoSync: true,
      });

    const finalGridOptions = React.useMemo(() => {
      if (preset) {
        const presetOptions = gridPresets[preset];
        return { ...presetOptions, ...gridOptions };
      } else if (features.length > 0) {
        return { ...createGridOptions(features), ...gridOptions };
      } else {
        return { ...defaultGridOptions, ...gridOptions };
      }
    }, [preset, features, gridOptions]);

  const buildColumnDefsFromApi = () => {
  if (!gridApi) return columnDefs;
  const columnState = gridApi.getColumnState();
  const allCols = gridApi.getAllGridColumns();

  // Create a mapping from colId -> state for quick lookup
  const stateMap = (columnState || []).reduce((m, s) => {
    m[s.colId] = s;
    return m;
  }, {});

  // Build updated defs based on current original columnDefs (so we preserve other props)
  return (columnDefs || []).map((col) => {
    // find matching active column by field or colId
    const colObj = allCols.find((c) => {
      const def = c.getColDef();
      // prefer match by field, fallback to colId
      return def.field === col.field || c.getColId() === col.colId || def.colId === col.colId;
    });

    const state = colObj ? stateMap[colObj.getColId()] : undefined;

    return {
      ...col,
      // state?.hide might be undefined; default to previous col.hide or false
      hide: state?.hide ?? col.hide ?? false,
      width: state?.width ?? col.width,
      pinned: state?.pinned ?? col.pinned,
      // keep other col props intact
    };
  });
};


    // useEffect(() => {
    //   console.log(
    //     "🔷 AGGridWrapper - columnDefs changed:",
    //     columnDefs.map((c) => ({ field: c.field, hide: c.hide }))
    //   );
    // }, [columnDefs]);

    const isBasicPreset = preset === 'basic';
    // Process column definitions to apply automatic rules
    const processedColumnDefs = useMemo(() => {
      return columnDefs.map((col) => {
        // Auto-configure columns without field (like actions)
        if (col.colId === "actions") {
          return {
            ...col,
            suppressColumnsToolPanel: true,
            suppressMovable: true,
            filter: false,
            sortable: false,
          };
        }

        if (
          disabledFields?.includes(col.headerName) ||
          disabledFields?.includes(col.field)
        ) {
          return {
            ...col,
            lockVisible: true,
          };
        }

        // For basic preset
        if (isBasicPreset) {
          return {
            ...col,
            suppressMovable: false,
            suppressMenu: true,
            resizable: true,
            suppressHeaderMenuButton: true
          };
        }

        return {
          ...col,
          suppressMovable: false,
        };
      });
    }, [columnDefs, isBasicPreset]);

    const handleGridReady = (params) => {
      setGridApi(params.api);

      // Skip wrapper-owned auto-sizing entirely when the consumer wants
      // exclusive control of column widths.
      if (disableAutoSize) {
        if (onGridReady) onGridReady(params);
        return;
      }

      // Handle auto-sizing based on column configuration
      const hasFlexColumns = columnDefs.some(col => col.flex !== undefined);

      if (!hasFlexColumns) {
        // Traditional width-based columns - use autoSizeAllColumns
        params.api.autoSizeAllColumns();
      } else {
        // For flex columns, auto-size them first to get proper widths
        // This prevents column headers from wrapping
        setTimeout(() => {
          try {
            // First, auto-size all flex columns to fit their content and headers
            const flexColumnIds = columnDefs
              .filter(colDef => colDef.flex && colDef.field)
              .map(colDef => colDef.field);
            
            if (flexColumnIds.length > 0) {
              // Auto-size columns to fit content + header
              params.api.autoSizeColumns(flexColumnIds, false);
            }
          } catch (error) {
            console.log('Error auto-sizing columns:', error);
          }
        }, 150); // Small delay to ensure data is rendered
      }

      if (onGridReady) {
        onGridReady(params);
      }
    };

    // Auto-size flex columns when data changes
    useEffect(() => {
      if (!gridApi || !rowData) return;
      if (disableAutoSize) return;

      const hasFlexColumns = columnDefs.some(col => col.flex !== undefined);
      if (hasFlexColumns) {
        setTimeout(() => {
          try {
            const flexColumnIds = columnDefs
              .filter(colDef => colDef.flex && colDef.field)
              .map(colDef => colDef.field);
            
            if (flexColumnIds.length > 0) {
              gridApi.autoSizeColumns(flexColumnIds, false);
            }
          } catch (error) {
            console.log('Error auto-sizing columns on data change:', error);
          }
        }, 100);
      }
    }, [gridApi, rowData, columnDefs]);

    useEffect(() => {
      if (!gridApi) return;

      if (isBasicPreset) return;

      const existingGetContextMenuItems = finalGridOptions.getContextMenuItems;

      const customContextMenu = (contextParams) => {
        let items = [];

        if (typeof existingGetContextMenuItems === "function") {
          items = existingGetContextMenuItems(contextParams);
        } else if (Array.isArray(existingGetContextMenuItems)) {
          items = [...existingGetContextMenuItems];
        } else {
          items = [
            "autoSizeAll",
            "separator",
            "copy",
            "copyWithHeaders",
            "separator",
            "resetColumns",
            "separator",
            "export",
          ];
        }

        items = items.map((item) => {
          if (item === "resetColumns") {
            return {
              name: "Reset Columns",
              action: () => {
                //console.log("🔴 Reset Columns clicked");
                resetColumns();
              },
            };
          }
          return item;
        });

        return items;
      };

      gridApi.setGridOption("getContextMenuItems", customContextMenu);
    }, [gridApi, resetColumns, finalGridOptions]);

    useEffect(() => {
      if (
        Array.isArray(initialColumnDefs) &&
        initialColumnDefs.length > 0 &&
        columnDefs.length === 0
      ) {
        setColumnDefs(initialColumnDefs);
      }
    }, [initialColumnDefs, columnDefs.length]);

    // const handleColumnChanged = (event) => {
    //   if (event.type === "toolPanelVisibleChanged" && !event.visible) {
    //     if (isManageColumnsEnabled) {
    //       syncColumnsImmediate(false);
    //     }
    //   }

    //   if (onColumnChanged) {
    //     onColumnChanged(event);
    //   }
    // };
  const handleColumnChanged = (event) => {
  if (!gridApi) return;

  // Sync React columnDefs state with current AG Grid columns
  const allColumns = gridApi.getAllGridColumns();
  const columnState = gridApi.getColumnState();

  const updatedDefs = allColumns.map((col) => {
    const def = col.getColDef();
    const state = columnState.find((s) => s.colId === col.getColId());
    return {
      ...def,
      hide: state?.hide ?? false,
      width: state?.width ?? def.width,
      pinned: state?.pinned ?? null,
      order: state?.order ?? 0,
    };
  });

  // FILTER OUT auto group columns so they are not persisted in state
  const filteredDefs = updatedDefs.filter(
    (d) =>
      !(
        d &&
        typeof d.colId === "string" &&
        d.colId.startsWith("ag-Grid-AutoColumn")
      ) && d.headerName !== "Group"
  );

  // Update the React state (only if changed to reduce re-renders)
  setColumnDefs((prev) => {
    // quick shallow compare by length & fields (cheap guard)
    if (!prev || prev.length !== filteredDefs.length) return filteredDefs;
    const prevIds = prev.map((p) => p.colId || p.field || p.headerName).join("|");
    const newIds = filteredDefs.map((p) => p.colId || p.field || p.headerName).join("|");
    if (prevIds !== newIds) return filteredDefs;
    // fallback to still update widths if they changed:
    const prevWidths = prev.map((p) => p.width).join("|");
    const newWidths = filteredDefs.map((p) => p.width).join("|");
    if (prevWidths !== newWidths) return filteredDefs;
    return prev;
  });

  // If AG Grid's manage column panel just closed, save immediately
  if (event.type === "toolPanelVisibleChanged" && !event.visible) {
    if (isManageColumnsEnabled) {
      syncColumnsImmediate(false);
    }
    
    // Trigger auto-sizing after manage columns is closed
    triggerAutoSizing();
  }

  // Trigger auto-sizing on column visibility changes
  if (event.type === "columnVisible") {
    // Small delay to allow column to be fully shown/hidden
    setTimeout(() => {
      triggerAutoSizing();
    }, 50);
  }

  // Forward event to parent if provided
  if (onColumnChanged) {
    onColumnChanged(event);
  }
};

    const triggerAutoSizing = () => {
      if (!gridApi) return;
      if (disableAutoSize) return;

      const hasFlexColumns = columnDefs.some(col => col.flex !== undefined);
      
      if (!hasFlexColumns) {
        // Traditional width-based columns - use autoSizeAllColumns
        try {
          gridApi.autoSizeAllColumns();
        } catch (error) {
          console.log('Error auto-sizing all columns:', error);
        }
      } else {
        // For flex columns, auto-size them to fit content and headers
        try {
          const visibleFlexColumns = columnDefs
            .filter(colDef => colDef.flex && colDef.field && !colDef.hide)
            .map(colDef => colDef.field);
          
          if (visibleFlexColumns.length > 0) {
            gridApi.autoSizeColumns(visibleFlexColumns, false);
          }
        } catch (error) {
          console.log('Error auto-sizing flex columns:', error);
        }
      }
    };
const handleRowGroupChanged = (event) => {
  if (!gridApi) return;

  // get current rowGroup columns (ColumnApi method)
  const rowGroupCols = gridApi.getRowGroupColumns();

  // If there are no grouped columns left, remove AG Grid's auto-group column(s)
  if (!rowGroupCols || rowGroupCols.length === 0) {
    try {
      // clear AG Grid auto group definition
      gridApi.setAutoGroupColumnDef(null);

      // Clean out any auto-generated auto-group columns from the React state and grid
      // We filter by colId pattern and headerName "Group"
      const cleanDefs = (gridApi.getColumnDefs() || []).filter(
        (c) =>
          !(c && c.colId && typeof c.colId === "string" && c.colId.startsWith("ag-Grid-AutoColumn")) &&
          c.headerName !== "Group"
      );

      // push cleaned defs both to grid and to local state
      // Only set if different length to avoid unnecessary re-render
      if (cleanDefs.length !== (columnDefs || []).length) {
        setColumnDefs(cleanDefs);
        // also ensure grid shows same defs
        gridApi.setColumnDefs(cleanDefs);
      }
    } catch (err) {
      // fail gracefully
      // console.warn("handleRowGroupChanged cleanup failed", err);
    }
  }
};
//   const handleColumnChanged = (event) => {
//   if (!gridApi) return;

//   // Sync React columnDefs state with current AG Grid columns
//   const allColumns = gridApi.getAllGridColumns();
//   const columnState = gridApi.getColumnState();

//   const updatedDefs = allColumns.map((col) => {
//     const def = col.getColDef();
//     const state = columnState.find((s) => s.colId === col.getColId());
//     return {
//       ...def,
//       hide: state?.hide ?? false,
//       width: state?.width ?? def.width,
//       pinned: state?.pinned ?? null,
//       order: state?.order ?? 0,
//     };
//   });

//   // Update the React state
//   setColumnDefs(updatedDefs);

//   // If AG Grid’s manage column panel just closed, save immediately
//   if (event.type === "toolPanelVisibleChanged" && !event.visible) {
//     if (isManageColumnsEnabled) {
//       syncColumnsImmediate(false);
//     }
//   }

//   // Forward event to parent if provided
//   if (onColumnChanged) {
//     onColumnChanged(event);
//   }
// };


    useImperativeHandle(ref, () => ({
      gridApi,
      gridRef: gridRef.current,
      openManageColumns: () => setShowManageColumnPopup(true),
      syncColumns: syncColumnsImmediate,
      resetColumns,
    }));

    return (
      <>
        {isManageColumnsEnabled && (
          <ManageColumnsPicker
            disabledFields={disabledFields}
            clientId={clientId}
            entityName={entityName}
            isTableView={false}
            setColumnDefs={setColumnDefs}
            columns={columnDefs}
            show={showManageColumnPopup}
            orginalColumns={initialColumnDefs}
            lockedColumnsSeparator={lockedColumnsSeparator}
            onHide={() => setShowManageColumnPopup(false)}
          />
        )}

        <AgGridReact
          ref={gridRef}
          {...finalGridOptions}
          columnDefs={processedColumnDefs}
          rowData={rowData}
          onGridReady={handleGridReady}
          onColumnVisible={handleColumnChanged}
          onColumnMoved={handleColumnChanged}
          onColumnPinned={handleColumnChanged}
          onToolPanelVisibleChanged={handleColumnChanged}
            onColumnRowGroupChanged={handleRowGroupChanged}  
          {...restProps}
        />
      </>
    );
  }
);

AGGridWrapper.displayName = "AGGridWrapper";

export default AGGridWrapper;
